/**
 * B7 · Nearby-distance merge — pure helpers (no I/O).
 *
 * The `nearby_profiles` RPC returns a server-fuzzed distance keyed by auth
 * user id (never raw coordinates). These helpers turn those rows into a
 * lookup and overlay the real distance onto already-adapted profiles, keyed
 * by `ownerId` (= app_profiles.owner_id = auth.users.id). Any profile without
 * a match keeps its existing (synthetic) distance — fail-open, never blank.
 */
import { formatDistance } from './geoDistance';

export interface NearbyRow {
  user_id: string;
  fuzzed_distance_km: number;
}

/**
 * Reduce RPC rows to an ownerId → fuzzed-distance(km) lookup. Malformed rows
 * (missing id, non-finite distance) are skipped so one bad row can't poison
 * the map. Pass `excludeId` to drop the caller's own row — the RPC returns the
 * caller at ~0 km because they upsert their own position into hottr_geo.
 */
export const toDistanceMap = (
  rows: NearbyRow[] | null | undefined,
  excludeId?: string,
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const row of rows ?? []) {
    if (!row || typeof row.user_id !== 'string' || row.user_id === '') continue;
    if (excludeId && row.user_id === excludeId) continue;
    if (!Number.isFinite(row.fuzzed_distance_km)) continue;
    map.set(row.user_id, row.fuzzed_distance_km);
  }
  return map;
};

/**
 * Overlay real (server-fuzzed) distances onto profiles by ownerId. Profiles
 * without a match — including ownerless demo/seed rows — are returned
 * untouched (keep their synthetic distance). When there's nothing to merge the
 * input array is returned by reference (fail-open, render-stable).
 */
export const mergeDistances = <T extends { ownerId?: string; distance: string }>(
  profiles: T[],
  distances: Map<string, number> | null | undefined,
): T[] => {
  if (!distances || distances.size === 0) return profiles;
  return profiles.map((p) =>
    p.ownerId && distances.has(p.ownerId)
      ? { ...p, distance: formatDistance(distances.get(p.ownerId)) }
      : p,
  );
};
